/* eslint-disable jira/filename-case */
/**
 * Use to remove `TS2339: Property 'id' does not exist on type 'object'` errors, e.g. when checking/refining variable of `unknown' type
 */
export const hasProp = <K extends PropertyKey>(data: object, prop: K): data is Record<K, unknown> =>
	prop in data;

/**
 * To mark some fields in the existing type as optional
 * Useful, for example, to type withDefaultData() wrapper components
 *
 * Example:
 * PartialBy<{ a: string; b: string; c: string }, 'b' | 'c'> equals to { a: string; b?: string; c?: string }
 */
export type PartialBy<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;

/**
 * If you have an object with typed keys (e.g. Record<MyKeyType, string>), then Object.keys() will still return string[],
 * not allowing to use those keys to read from the same object (showing Typescript error)!
 * Example: https://www.typescriptlang.org/play/?#code/KYDwDg9gTgLgBAYwgOwM7wNIFECaB9ARjgF44ByAa2AE8Cy4BDVRFdAbgChRJYW1NceAEwlyVakPpM+7LuGjwY1MMDgYaqACrLVxDgEgAPnCUqIAMzWCCB46eAWr+IRzk94SfnAgAjAFYA6gCWMAAW2ioAJurUqABccABKwEhQkQA8MVo6ADRw6FBByADmAHyiAN4GANrY+AQAuglkDGQ5NXXCTeQ+bXBwHAC+rp7o3v4iogDy-ikwAHTiqAAUvoEh4TrRGgCU81DAkQCuCMDLywwICAkMyNR54jsk5VX6lwjV4g2ia8FhEYcsp8aA02PoDAcYEcoMhGFdOIM8hVBjtOEA
 *
 * The only known way atm to fix this, and have `key: MyKeyType` - is to use `as` forced type conversion, which is not type safe to do. Also prohibited by eslint in jira.
 * Therefore, it is better to do it in one place, in a safe manner (e.g. using `keyof`, avoiding bugs due to a wrong key type being used on the right side of `as`).
 */
export const getTypedKeys = <T extends object>(obj: T): Array<keyof T> => {
	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	return Object.keys(obj) as Array<keyof T>;
};
